import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, CircularProgress, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Stack, InputAdornment,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import {
    FaCalculator, FaSearch, FaPlus, FaEdit, FaTrash, FaClone, FaLock, FaCheckCircle
} from 'react-icons/fa';
import {
    getStandardCosts, addStandardCostItem, updateStandardCostItem,
    deleteStandardCostItem, duplicateStandardCosts
} from '../api/api';
import { useCompetence } from '../contexts/CompetenceContext';

const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const CustoPadraoPage = () => {
    const { globalCompetence, availableMonths, isCompetenceClosed } = useCompetence();

    const [competencias, setCompetencias] = useState([]);
    const [selectedCompetencia, setSelectedCompetencia] = useState('');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // CRUD Modal State
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ codigo: '', descricao: '', valor: '' });

    // Delete Modal State
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    // Duplication Modal State
    const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
    const [destCompetencia, setDestCompetencia] = useState('');
    const [duplicating, setDuplicating] = useState(false);
    const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

    const isClosed = isCompetenceClosed(selectedCompetencia);

    // Populate competence selector on mount/context load
    useEffect(() => {
        if (availableMonths.length > 0) {
            setCompetencias(availableMonths);
        }
    }, [availableMonths]);

    // Initial default competence from global context
    useEffect(() => {
        if (globalCompetence && competencias.length > 0 && !selectedCompetencia) {
            if (competencias.includes(globalCompetence)) {
                setSelectedCompetencia(globalCompetence);
            }
        }
    }, [globalCompetence, competencias, selectedCompetencia]);

    // Fetch items when competence, page or search changes
    useEffect(() => {
        if (selectedCompetencia) {
            fetchItems();
        } else {
            setItems([]);
            setTotal(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompetencia, page, search]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await getStandardCosts(selectedCompetencia, {
                page,
                search,
                per_page: 50
            });
            setItems(res.data.items);
            setTotal(res.data.total);
        } catch (error) {
            console.error("Erro ao carregar custos padrão:", error);
            setMessage({ type: 'error', text: 'Erro ao carregar custos padrão.' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({ codigo: '', descricao: '', valor: '' });
        setOpenModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            codigo: item.codigo,
            descricao: item.descricao,
            valor: item.valor.toString()
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        try {
            if (!selectedCompetencia) {
                setMessage({ type: 'error', text: 'Selecione uma competência primeiro.' });
                return;
            }
            if (!formData.codigo.trim()) {
                setMessage({ type: 'error', text: 'O Código é obrigatório.' });
                return;
            }
            if (!formData.descricao.trim()) {
                setMessage({ type: 'error', text: 'A Descrição é obrigatória.' });
                return;
            }
            if (formData.valor === '' || isNaN(parseFloat(formData.valor))) {
                setMessage({ type: 'error', text: 'Informe um valor numérico válido.' });
                return;
            }

            const payload = {
                codigo: formData.codigo.trim(),
                descricao: formData.descricao.trim(),
                valor: parseFloat(formData.valor)
            };

            if (editingItem) {
                await updateStandardCostItem(editingItem.id, payload);
                setMessage({ type: 'success', text: 'Custo padrão atualizado com sucesso!' });
            } else {
                await addStandardCostItem({ ...payload, competencia: selectedCompetencia });
                setMessage({ type: 'success', text: 'Custo padrão cadastrado com sucesso!' });
            }
            handleCloseModal();
            fetchItems();
        } catch (error) {
            console.error('Erro ao salvar item:', error);
            const errorMsg = error.response?.data?.mensagem || 'Erro ao salvar item.';
            setMessage({ type: 'error', text: errorMsg });
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            await deleteStandardCostItem(deleteConfirmation);
            setMessage({ type: 'success', text: 'Custo padrão excluído com sucesso!' });
            setDeleteConfirmation(null);
            fetchItems();
        } catch (error) {
            console.error('Erro ao excluir item:', error);
            setMessage({ type: 'error', text: 'Erro ao excluir item.' });
        }
    };

    const handleOpenDuplicate = () => {
        setDestCompetencia('');
        setConfirmOverwriteOpen(false);
        setOpenDuplicateModal(true);
    };

    const handleDuplicateSubmit = async (sobrescrever = false) => {
        if (!selectedCompetencia || !destCompetencia) {
            setMessage({ type: 'error', text: 'Selecione as competências de origem e destino.' });
            return;
        }

        setDuplicating(true);
        try {
            const res = await duplicateStandardCosts({
                origem: selectedCompetencia,
                destino: destCompetencia,
                sobrescrever
            });

            if (res.data.status === 'confirmacao_necessaria') {
                setConfirmOverwriteOpen(true);
            } else {
                setMessage({ type: 'success', text: res.data.mensagem });
                setOpenDuplicateModal(false);
                if (destCompetencia === selectedCompetencia) {
                    fetchItems();
                }
            }
        } catch (error) {
            console.error('Erro ao duplicar:', error);
            const errorMsg = error.response?.data?.mensagem || 'Erro ao duplicar registros.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }} className="page-fade-in">
            {/* Header */}
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 2, mb: 4 }} className="premium-gradient-text">
                <FaCalculator style={{ color: '#1976d2' }} /> Cadastro de Custo Padrão
            </Typography>

            {message && (
                <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {/* Selection Card */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Competência Selecionada</InputLabel>
                            <Select
                                value={selectedCompetencia}
                                label="Competência Selecionada"
                                onChange={(e) => {
                                    setSelectedCompetencia(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <MenuItem value=""><em>Selecione a Competência</em></MenuItem>
                                {competencias.map(comp => (
                                    <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar código ou descrição..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FaSearch color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Content Table / Grid */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                {isClosed && (
                    <Alert severity="warning" icon={<FaLock />} sx={{ mb: 3, borderRadius: 2 }}>
                        Esta competência está fechada. Mutações e alterações não são permitidas.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold">
                        Registros ({total})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {selectedCompetencia && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<FaClone />}
                                onClick={handleOpenDuplicate}
                                sx={{ borderRadius: 2 }}
                            >
                                Duplicar Tabela
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FaPlus />}
                            onClick={handleOpenAdd}
                            disabled={!selectedCompetencia || isClosed}
                            sx={{ borderRadius: 2 }}
                        >
                            Novo Registro
                        </Button>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : items.length === 0 ? (
                    <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 8 }}>
                        {selectedCompetencia ? 'Nenhum registro encontrado.' : 'Selecione uma competência para visualizar os dados.'}
                    </Typography>
                ) : (
                    <>
                        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Código</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Descrição</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', py: 1.5 }}>Valor Padrão</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: 120, py: 1.5 }}>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell sx={{ fontWeight: 'medium' }}>{item.codigo}</TableCell>
                                            <TableCell>{item.descricao}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                {formatCurrency(item.valor)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenEdit(item)}
                                                    disabled={isClosed}
                                                    title="Editar registro"
                                                >
                                                    <FaEdit size={16} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeleteConfirmation(item.id)}
                                                    disabled={isClosed}
                                                    title="Excluir registro"
                                                >
                                                    <FaTrash size={14} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                                Mostrando {items.length} de {total} registros
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    sx={{ borderRadius: 1.5 }}
                                >
                                    Anterior
                                </Button>
                                <Typography variant="body2">
                                    Página {page}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={items.length < 50}
                                    sx={{ borderRadius: 1.5 }}
                                >
                                    Próxima
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Paper>

            {/* Add/Edit Dialog Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Editar Registro de Custo Padrão' : 'Novo Custo Padrão'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            label="Código"
                            fullWidth
                            size="small"
                            value={formData.codigo}
                            onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                        />
                        <TextField
                            label="Descrição"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        />
                        <TextField
                            label="Valor"
                            type="number"
                            fullWidth
                            size="small"
                            value={formData.valor}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseModal} variant="outlined" sx={{ borderRadius: 2 }}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ borderRadius: 2 }}>Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={!!deleteConfirmation} onClose={() => setDeleteConfirmation(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza de que deseja excluir este registro de custo padrão? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteConfirmation(null)} variant="outlined" sx={{ borderRadius: 2 }}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{ borderRadius: 2 }}>Excluir</Button>
                </DialogActions>
            </Dialog>

            {/* Duplication Modal */}
            <Dialog open={openDuplicateModal} onClose={() => !duplicating && setOpenDuplicateModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaClone color="#1976d2" /> Duplicar Tabela
                </DialogTitle>
                <DialogContent>
                    {!confirmOverwriteOpen ? (
                        <>
                            <DialogContentText sx={{ mb: 3 }}>
                                Copie todos os custos padrão da competência <strong>{selectedCompetencia}</strong> para outra competência de destino.
                            </DialogContentText>
                            <FormControl fullWidth size="small">
                                <InputLabel>Competência de Destino</InputLabel>
                                <Select
                                    value={destCompetencia}
                                    label="Competência de Destino"
                                    onChange={(e) => setDestCompetencia(e.target.value)}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {competencias.filter(c => c !== selectedCompetencia).map(comp => (
                                        <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    ) : (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body1" sx={{ color: 'warning.main', fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <FaCheckCircle /> Registros Existentes no Destino
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Já existem registros de custo padrão cadastrados para a competência de destino ({destCompetencia}). Tem certeza que deseja substituí-los?
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    {!confirmOverwriteOpen ? (
                        <>
                            <Button onClick={() => setOpenDuplicateModal(false)} disabled={duplicating} variant="outlined" sx={{ borderRadius: 2 }}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => handleDuplicateSubmit(false)}
                                disabled={duplicating || !destCompetencia}
                                variant="contained"
                                sx={{ borderRadius: 2 }}
                            >
                                {duplicating ? 'Duplicando...' : 'Duplicar'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setConfirmOverwriteOpen(false)} disabled={duplicating} variant="outlined" sx={{ borderRadius: 2 }}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => handleDuplicateSubmit(true)}
                                disabled={duplicating}
                                color="error"
                                variant="contained"
                                sx={{ borderRadius: 2 }}
                            >
                                {duplicating ? 'Sobrescrevendo...' : 'Confirmar e Sobrescrever'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustoPadraoPage;
